import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Search,
  Filter,
  Download,
  Eye,
  SearchIcon,
  Lightbulb,
  CheckCircle,
  Pencil,
  MessageSquare,
  Settings,
  Users,
  User,
  Bot,
  Server,
} from 'lucide-react'
import type { EntryCategory, ActorType } from '../../types/history'

const categoryConfig: Record<EntryCategory, { icon: typeof Eye; color: string }> = {
  view: { icon: Eye, color: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400' },
  analysis: {
    icon: SearchIcon,
    color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  },
  finding: {
    icon: Lightbulb,
    color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  },
  decision: {
    icon: CheckCircle,
    color: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
  },
  edit: {
    icon: Pencil,
    color: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400',
  },
  annotation: {
    icon: MessageSquare,
    color: 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-400',
  },
  export: {
    icon: Download,
    color: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400',
  },
  config: {
    icon: Settings,
    color: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-400',
  },
  collaboration: {
    icon: Users,
    color: 'bg-pink-100 text-pink-700 dark:bg-pink-900/30 dark:text-pink-400',
  },
}

const actorConfig: Record<ActorType, { icon: typeof User; color: string }> = {
  user: { icon: User, color: 'text-blue-600 dark:text-blue-400' },
  ai: { icon: Bot, color: 'text-indigo-600 dark:text-indigo-400' },
  system: { icon: Server, color: 'text-slate-500 dark:text-slate-400' },
}

interface TimelineFiltersProps {
  searchQuery: string
  onSearchChange: (value: string) => void
  selectedCategories: EntryCategory[]
  onCategoriesChange: (categories: EntryCategory[]) => void
  selectedActors: ActorType[]
  onActorsChange: (actors: ActorType[]) => void
  showSystemEvents: boolean
  onShowSystemEventsChange: (show: boolean) => void
}

function CategoryButtons({
  selected,
  onChange,
}: {
  selected: EntryCategory[]
  onChange: (cats: EntryCategory[]) => void
}): React.ReactElement {
  const { t } = useTranslation('history')
  return (
    <div className="flex flex-wrap gap-2">
      {(Object.keys(categoryConfig) as EntryCategory[]).map((cat) => {
        const config = categoryConfig[cat]
        const isSelected = selected.includes(cat)
        return (
          <button
            key={cat}
            onClick={() => {
              onChange(isSelected ? selected.filter((item) => item !== cat) : [...selected, cat])
            }}
            className={`rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${
              isSelected
                ? config.color
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-400 dark:hover:bg-slate-700'
            }`}
          >
            {t(`categories.${cat}` as const)}
          </button>
        )
      })}
    </div>
  )
}

function ActorButtons({
  selected,
  onChange,
}: {
  selected: ActorType[]
  onChange: (actors: ActorType[]) => void
}): React.ReactElement {
  const { t } = useTranslation('history')
  return (
    <div className="flex flex-wrap gap-2">
      {(Object.keys(actorConfig) as ActorType[]).map((actor) => {
        const config = actorConfig[actor]
        const ActIcon = config.icon
        const isSelected = selected.includes(actor)
        return (
          <button
            key={actor}
            onClick={() => {
              onChange(
                isSelected ? selected.filter((item) => item !== actor) : [...selected, actor]
              )
            }}
            className={`flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${
              isSelected
                ? 'bg-[var(--accent,#4e5e82)] text-white'
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-400 dark:hover:bg-slate-700'
            }`}
          >
            <ActIcon className="h-3.5 w-3.5" />
            {t(`actors.${actor}` as const)}
          </button>
        )
      })}
    </div>
  )
}

function SearchBar({
  query,
  onChange,
}: {
  query: string
  onChange: (value: string) => void
}): React.ReactElement {
  const { t } = useTranslation('history')
  return (
    <div className="relative flex-1">
      <Search className="absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-slate-400" />
      <input
        type="text"
        placeholder={t('searchPlaceholder')}
        value={query}
        onChange={(ev) => {
          onChange(ev.target.value)
        }}
        className="w-full rounded-lg border border-slate-200 bg-slate-50 py-2 pr-4 pl-10 text-slate-900 placeholder-slate-400 focus:border-transparent focus:ring-2 focus:ring-[var(--accent,#4e5e82)] focus:outline-none dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
      />
    </div>
  )
}

function FilterToggle({
  active,
  count,
  onClick,
}: {
  active: boolean
  count: number
  onClick: () => void
}): React.ReactElement {
  const { t } = useTranslation('history')
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-2 rounded-lg border px-4 py-2 transition-colors ${
        active || count > 0
          ? 'border-[var(--accent,#4e5e82)] bg-slate-50 text-[var(--accent,#4e5e82)] dark:bg-slate-800'
          : 'border-slate-200 text-slate-600 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-400 dark:hover:bg-slate-800'
      }`}
    >
      <Filter className="h-4 w-4" />
      {t('filters')}
      {count > 0 && (
        <span className="rounded-full bg-[var(--accent,#4e5e82)] px-1.5 py-0.5 text-xs text-white">
          {count}
        </span>
      )}
    </button>
  )
}

export function TimelineFilters(props: TimelineFiltersProps): React.ReactElement {
  const { t } = useTranslation('history')
  const [showFilters, setShowFilters] = useState(false)
  const activeFilterCount =
    props.selectedCategories.length + props.selectedActors.length + (props.showSystemEvents ? 1 : 0)

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900">
      <div className="flex items-center gap-4">
        <SearchBar query={props.searchQuery} onChange={props.onSearchChange} />
        <label className="flex cursor-pointer items-center gap-2 text-sm whitespace-nowrap text-slate-600 select-none dark:text-slate-400">
          <input
            type="checkbox"
            checked={props.showSystemEvents}
            onChange={(ev) => {
              props.onShowSystemEventsChange(ev.target.checked)
            }}
            className="rounded border-slate-300 text-[var(--accent,#4e5e82)] focus:ring-[var(--accent,#4e5e82)] dark:border-slate-600"
          />
          {t('showSystemEvents')}
        </label>
        <FilterToggle
          active={showFilters}
          count={activeFilterCount}
          onClick={() => {
            setShowFilters(!showFilters)
          }}
        />
      </div>
      {showFilters && (
        <div className="mt-4 space-y-4 border-t border-slate-100 pt-4 dark:border-slate-800">
          <div>
            <p className="mb-2 text-xs font-medium text-slate-500 dark:text-slate-400">
              {t('categoryLabel')}
            </p>
            <CategoryButtons
              selected={props.selectedCategories}
              onChange={props.onCategoriesChange}
            />
          </div>
          <div>
            <p className="mb-2 text-xs font-medium text-slate-500 dark:text-slate-400">
              {t('actorLabel')}
            </p>
            <ActorButtons selected={props.selectedActors} onChange={props.onActorsChange} />
          </div>
        </div>
      )}
    </div>
  )
}
